import React from 'react';
import { Info, HelpCircle } from 'lucide-react';

const ScoreGuidelines = ({ showFullDetails = false }) => {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex items-start mb-3">
        <Info className="h-5 w-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
        <h3 className="text-lg font-medium text-gray-900">
          Understanding Our Scoring System
        </h3>
      </div>
      
      <div className="flex items-center mb-4 bg-blue-50 p-2 rounded-md border border-blue-100">
        <div className="text-sm text-blue-800">
          <span className="font-semibold">How to read our scores:</span> Our analysis uses 0-100 scores where <span className="font-semibold">higher numbers = greater accuracy</span>. 
          Lower scores indicate more misleading content.
        </div>
      </div>
      
      {/* Dual scale explanation */}
      <div className="bg-gray-100 p-3 rounded-md mb-4 border border-gray-200">
        <h4 className="text-sm font-medium text-gray-800 mb-2">Understanding Our Two Score Scales</h4>
        <div className="flex flex-col md:flex-row">
          <div className="flex-1 p-2">
            <div className="font-semibold text-green-700 text-sm">Truth Score (Higher = More Accurate)</div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-1 mb-2 overflow-hidden">
              <div className="bg-gradient-to-r from-red-500 via-yellow-400 to-green-600 h-2 rounded-full" style={{ width: "100%" }}></div>
            </div>
            <div className="flex justify-between text-xs text-gray-600">
              <span>0 (Less Accurate)</span>
              <span>100 (Most Accurate)</span>
            </div>
          </div>
          <div className="hidden md:block border-r border-gray-300 mx-2"></div>
          <div className="flex-1 p-2">
            <div className="font-semibold text-red-700 text-sm">Misinformation Level (Higher = More Misleading)</div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-1 mb-2 overflow-hidden">
              <div className="bg-gradient-to-r from-green-600 via-yellow-400 to-red-500 h-2 rounded-full" style={{ width: "100%" }}></div>
            </div>
            <div className="flex justify-between text-xs text-gray-600">
              <span>0 (Not Misleading)</span>
              <span>100 (Very Misleading)</span>
            </div>
          </div>
        </div>
      </div>
      
      <p className="text-sm text-gray-700 mb-4">
        Our analysis is based on four key metrics, each scored from 0-100, with an overall combined score.
        Higher scores indicate greater accuracy and credibility.
      </p>
      
      <div className="space-y-4">
        <div>
          <div className="flex items-center">
            <h4 className="text-sm font-medium text-gray-800 mb-1">Truthfulness (30% of total)</h4>
            <div className="relative group ml-1">
              <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
              <div className="absolute z-10 hidden group-hover:block bg-white p-2 rounded shadow-lg border border-gray-200 w-64 text-xs text-gray-700 top-full left-0">
                How factually accurate are the claims in this content? Higher scores mean more factual accuracy.
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-600 mb-2">
            Measures the factual accuracy of claims and statements in the content.
          </p>
          
          {showFullDetails && (
            <div className="grid grid-cols-2 gap-2 text-xs mb-2">
              <div className="bg-red-50 text-red-800 p-1 rounded">
                <span className="font-medium">0-30:</span> Misleading/false claims
              </div>
              <div className="bg-yellow-50 text-yellow-800 p-1 rounded">
                <span className="font-medium">31-60:</span> Somewhat misleading
              </div>
              <div className="bg-green-50 text-green-800 p-1 rounded">
                <span className="font-medium">61-80:</span> Mostly accurate
              </div>
              <div className="bg-green-50 text-green-800 p-1 rounded">
                <span className="font-medium">81-100:</span> Highly accurate
              </div>
            </div>
          )}
          
          {/* Horizontal score bar with directional indicators */}
          <div className="relative w-full mb-1">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-red-600">Less Accurate</span>
              <span className="text-green-600">More Accurate</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 flex overflow-hidden">
              <div className="bg-red-500 h-2.5 flex-none rounded-l-full" style={{ width: "30%" }}></div>
              <div className="bg-yellow-400 h-2.5 flex-none" style={{ width: "30%" }}></div>
              <div className="bg-green-400 h-2.5 flex-none" style={{ width: "20%" }}></div>
              <div className="bg-green-600 h-2.5 flex-none rounded-r-full" style={{ width: "20%" }}></div>
            </div>
            <div className="flex justify-between text-xs mt-1">
              <span>0</span>
              <span>30</span>
              <span>60</span>
              <span>80</span>
              <span>100</span>
            </div>
          </div>
        </div>
        
        <div>
          <div className="flex items-center">
            <h4 className="text-sm font-medium text-gray-800 mb-1">Constitutional Accuracy (25% of total)</h4>
            <div className="relative group ml-1">
              <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
              <div className="absolute z-10 hidden group-hover:block bg-white p-2 rounded shadow-lg border border-gray-200 w-64 text-xs text-gray-700 top-full left-0">
                How accurately does this content represent constitutional principles and processes? Higher scores indicate more accurate representation.
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-600 mb-2">
            Measures how accurately the content represents constitutional principles, processes, and facts.
          </p>
          
          {showFullDetails && (
            <div className="grid grid-cols-2 gap-2 text-xs mb-2">
              <div className="bg-red-50 text-red-800 p-1 rounded">
                <span className="font-medium">0-30:</span> Misleading representation
              </div>
              <div className="bg-yellow-50 text-yellow-800 p-1 rounded">
                <span className="font-medium">31-60:</span> Somewhat misleading
              </div>
              <div className="bg-green-50 text-green-800 p-1 rounded">
                <span className="font-medium">61-80:</span> Mostly accurate
              </div>
              <div className="bg-green-50 text-green-800 p-1 rounded">
                <span className="font-medium">81-100:</span> Highly accurate
              </div>
            </div>
          )}
          
          {/* Horizontal score bar with directional indicators */}
          <div className="relative w-full mb-1">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-red-600">Less Accurate</span>
              <span className="text-green-600">More Accurate</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 flex overflow-hidden">
              <div className="bg-red-500 h-2.5 flex-none rounded-l-full" style={{ width: "30%" }}></div>
              <div className="bg-yellow-400 h-2.5 flex-none" style={{ width: "30%" }}></div>
              <div className="bg-green-400 h-2.5 flex-none" style={{ width: "20%" }}></div>
              <div className="bg-green-600 h-2.5 flex-none rounded-r-full" style={{ width: "20%" }}></div>
            </div>
            <div className="flex justify-between text-xs mt-1">
              <span>0</span>
              <span>30</span>
              <span>60</span>
              <span>80</span>
              <span>100</span>
            </div>
          </div>
        </div>
        
        <div>
          <div className="flex items-center">
            <h4 className="text-sm font-medium text-gray-800 mb-1">Source Credibility (25% of total)</h4>
            <div className="relative group ml-1">
              <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
              <div className="absolute z-10 hidden group-hover:block bg-white p-2 rounded shadow-lg border border-gray-200 w-64 text-xs text-gray-700 top-full left-0">
                How reliable are the sources cited in this content? Higher scores indicate more credible and diverse sources.
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-600 mb-2">
            Evaluates the reliability and credibility of sources cited or used in the content.
          </p>
          
          {showFullDetails && (
            <div className="grid grid-cols-2 gap-2 text-xs mb-2">
              <div className="bg-red-50 text-red-800 p-1 rounded">
                <span className="font-medium">0-30:</span> Unreliable sources
              </div>
              <div className="bg-yellow-50 text-yellow-800 p-1 rounded">
                <span className="font-medium">31-60:</span> Mixed credibility
              </div>
              <div className="bg-green-50 text-green-800 p-1 rounded">
                <span className="font-medium">61-80:</span> Generally reliable
              </div>
              <div className="bg-green-50 text-green-800 p-1 rounded">
                <span className="font-medium">81-100:</span> Highly credible
              </div>
            </div>
          )}
          
          {/* Horizontal score bar with directional indicators */}
          <div className="relative w-full mb-1">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-red-600">Less Credible</span>
              <span className="text-green-600">More Credible</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 flex overflow-hidden">
              <div className="bg-red-500 h-2.5 flex-none rounded-l-full" style={{ width: "30%" }}></div>
              <div className="bg-yellow-400 h-2.5 flex-none" style={{ width: "30%" }}></div>
              <div className="bg-green-400 h-2.5 flex-none" style={{ width: "20%" }}></div>
              <div className="bg-green-600 h-2.5 flex-none rounded-r-full" style={{ width: "20%" }}></div>
            </div>
            <div className="flex justify-between text-xs mt-1">
              <span>0</span>
              <span>30</span>
              <span>60</span>
              <span>80</span>
              <span>100</span>
            </div>
          </div>
        </div>
        
        <div>
          <div className="flex items-center">
            <h4 className="text-sm font-medium text-gray-800 mb-1">Rhetoric Analysis (20% of total)</h4>
            <div className="relative group ml-1">
              <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
              <div className="absolute z-10 hidden group-hover:block bg-white p-2 rounded shadow-lg border border-gray-200 w-64 text-xs text-gray-700 top-full left-0">
                Does this content use fair presentation or manipulative tactics? Higher scores indicate more balanced presentation.
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-600 mb-2">
            Assesses the use of rhetorical tactics, logical fallacies, and manipulative techniques.
          </p>
          
          {showFullDetails && (
            <div className="grid grid-cols-2 gap-2 text-xs mb-2">
              <div className="bg-red-50 text-red-800 p-1 rounded">
                <span className="font-medium">0-30:</span> Manipulative rhetoric
              </div>
              <div className="bg-yellow-50 text-yellow-800 p-1 rounded">
                <span className="font-medium">31-60:</span> Somewhat manipulative
              </div>
              <div className="bg-green-50 text-green-800 p-1 rounded">
                <span className="font-medium">61-80:</span> Generally fair
              </div>
              <div className="bg-green-50 text-green-800 p-1 rounded">
                <span className="font-medium">81-100:</span> Balanced presentation
              </div>
            </div>
          )}
          
          {/* Horizontal score bar with directional indicators */}
          <div className="relative w-full mb-1">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-red-600">More Manipulative</span>
              <span className="text-green-600">More Balanced</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 flex overflow-hidden">
              <div className="bg-red-500 h-2.5 flex-none rounded-l-full" style={{ width: "30%" }}></div>
              <div className="bg-yellow-400 h-2.5 flex-none" style={{ width: "30%" }}></div>
              <div className="bg-green-400 h-2.5 flex-none" style={{ width: "20%" }}></div>
              <div className="bg-green-600 h-2.5 flex-none rounded-r-full" style={{ width: "20%" }}></div>
            </div>
            <div className="flex justify-between text-xs mt-1">
              <span>0</span>
              <span>30</span>
              <span>60</span>
              <span>80</span>
              <span>100</span>
            </div>
          </div>
        </div>
        
        <div>
          <div className="flex items-center">
            <h4 className="text-sm font-medium text-gray-800 mb-1">Overall Score</h4>
            <div className="relative group ml-1">
              <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
              <div className="absolute z-10 hidden group-hover:block bg-white p-2 rounded shadow-lg border border-gray-200 w-64 text-xs text-gray-700 top-full left-0">
                Combined weighted score from all categories. Higher scores indicate more accurate and reliable content overall.
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-600 mb-2">
            A weighted average of the four scores above: Truthfulness (30%), Constitutional Accuracy (25%), Source Credibility (25%), and Rhetoric Analysis (20%).
          </p>
          
          <div className="bg-gray-100 p-2 rounded mb-2">
            <div className="grid grid-cols-4 gap-1 text-xs">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-500 rounded-full mr-1"></div>
                <span>0-30: Misleading</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-yellow-500 rounded-full mr-1"></div>
                <span>31-60: Somewhat Misleading</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-400 rounded-full mr-1"></div>
                <span>61-80: Mostly Accurate</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-600 rounded-full mr-1"></div>
                <span>81-100: Accurate</span>
              </div>
            </div>
          </div>
          
          {/* Horizontal score bar with directional indicators */}
          <div className="relative w-full mb-1">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-red-600">Less Reliable</span>
              <span className="text-green-600">More Reliable</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 flex overflow-hidden">
              <div className="bg-red-500 h-3 flex-none rounded-l-full" style={{ width: "30%" }}></div>
              <div className="bg-yellow-400 h-3 flex-none" style={{ width: "30%" }}></div>
              <div className="bg-green-400 h-3 flex-none" style={{ width: "20%" }}></div>
              <div className="bg-green-600 h-3 flex-none rounded-r-full" style={{ width: "20%" }}></div>
            </div>
            <div className="flex justify-between text-xs mt-1">
              <span>0</span>
              <span>30</span>
              <span>60</span>
              <span>80</span>
              <span>100</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Learn more link removed */}
    </div>
  );
};

export default ScoreGuidelines;